/**
 * バックエンドAPIサービス
 * 10MB以上のファイル生成時にバックエンドAPIを呼び出す
 */

import { AppConfig } from '../config.js';

export interface BackendGenerationRequest {
  targetSize: number;
}

export interface BackendGenerationResponse {
  success: boolean;
  data?: {
    buffer: ArrayBuffer;
    actualSize: number;
    processingTime: number;
    accuracy: number;
    metadata: {
      width: number;
      height: number;
      quality: number;
      phases: string[];
    };
  };
  error?: {
    type: string;
    message: string;
    details?: any;
  };
}

export interface BackendHealthResponse {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  uptime: number;
  memory: {
    used: number;
    total: number;
  };
}

export interface BackendStatsResponse {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  averageProcessingTime: number;
  averageFileSize: number;
}

export class BackendService {
  private readonly baseUrl: string;
  private readonly timeout: number;

  constructor(baseUrl: string = 'http://localhost:3000', timeout: number = 120000) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // 末尾のスラッシュを削除
    this.timeout = timeout;
  }

  /**
   * バックエンドAPIで画像を生成する
   */
  async generateImage(targetSize: number): Promise<BackendGenerationResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(`${this.baseUrl}/api/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ targetSize }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: {
            type: this.mapHttpStatusToErrorType(response.status),
            message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            details: errorData
          }
        };
      }

      // レスポンスヘッダーから情報を取得
      const actualSize = parseInt(response.headers.get('X-Actual-Size') || '0');
      const processingTime = parseFloat(response.headers.get('X-Generation-Time') || '0');
      const accuracy = parseFloat(response.headers.get('X-Accuracy') || '0');
      const width = parseInt(response.headers.get('X-Width') || '0');
      const height = parseInt(response.headers.get('X-Height') || '0');
      const quality = parseFloat(response.headers.get('X-Quality') || '0');
      const phases = response.headers.get('X-Phases')?.split(',') || [];

      // バイナリデータを取得
      const buffer = await response.arrayBuffer();

      return {
        success: true,
        data: {
          buffer,
          actualSize,
          processingTime,
          accuracy,
          metadata: {
            width,
            height,
            quality,
            phases
          }
        }
      };

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: {
            type: 'timeout',
            message: `リクエストがタイムアウトしました (${this.timeout}ms)`
          }
        };
      }

      return {
        success: false,
        error: {
          type: 'network',
          message: error instanceof Error ? error.message : 'ネットワークエラーが発生しました',
          details: error
        }
      };
    }
  }

  /**
   * バックエンドAPIのヘルスチェック
   */
  async checkHealth(): Promise<BackendHealthResponse | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5秒でタイムアウト

      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn('Backend health check failed:', error);
      return null;
    }
  }

  /**
   * バックエンドAPIの統計情報を取得
   */
  async getStats(): Promise<BackendStatsResponse | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stats`, {
        method: 'GET',
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn('Failed to get backend stats:', error);
      return null;
    }
  }

  /**
   * バックエンドAPIが利用可能かチェック
   */
  async isAvailable(): Promise<boolean> {
    const health = await this.checkHealth();
    return health?.status === 'healthy';
  }

  /**
   * HTTPステータスコードをエラータイプにマッピング
   */
  private mapHttpStatusToErrorType(status: number): string {
    switch (status) {
      case 400:
        return 'validation';
      case 413:
        return 'memory';
      case 408:
      case 504:
        return 'timeout';
      case 500:
      case 502:
      case 503:
        return 'server';
      default:
        return 'network';
    }
  }

  /**
   * ArrayBufferをBlobに変換
   */
  static arrayBufferToBlob(buffer: ArrayBuffer, mimeType: string = 'image/jpeg'): Blob {
    return new Blob([buffer], { type: mimeType });
  }
}

// デフォルトインスタンス
export const backendService = new BackendService(AppConfig.backend.baseUrl, AppConfig.backend.timeout); 